import {AntibodyFormat, DistinctDomainKeyWithChainIndex} from 'antibodies/AntibodyTypedefs';
import {ImplementedAntibodySchemaInterface} from 'antibodies/hooks/useImplementedAntibodySchemaInterfacesQuery';
import {
  AntibodyChainInput,
  AntibodyDomainInput,
  AntibodyDomainPairInput,
  AntibodyPairAwareCreateInput,
  DistinctDomainKeyDict,
  DistinctChainKeyDict,
  AntibodyPairAwareCreateInputDict,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {NdcStartingType} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import * as ExportUtil from 'util/ExportUtil';
import {initiateTextDownload} from 'util/TextToBlobUtil';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

/**
 * Returns true if every constant domain has a DNA or AA component specified.
 */
export function areConstantDomainsFullyDefined(constantDomains: Array<AntibodyDomainInput>): boolean {
  return constantDomains.every((domain) => domain.dnaComponent != null || domain.aaComponent != null);
}

function buildSchemaIdToFormatMap(
  implementedInterfaces: Array<ImplementedAntibodySchemaInterface>
): Map<string, AntibodyFormatGraphQL> {
  return new Map(
    implementedInterfaces.map((implementedInterface) => [
      implementedInterface.schema.id,
      implementedInterface.format,
    ])
  );
}

function buildFormatIdToSchemaIdsMap(
  implementedInterfaces: Array<ImplementedAntibodySchemaInterface>
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const implementedInterface of implementedInterfaces) {
    const formatId = implementedInterface.format.id;
    if (!map.has(formatId)) {
      map.set(formatId, new Set());
    }
    map.get(formatId)!.add(implementedInterface.schema.id);
  }
  return map;
}

function buildAllSchemaIdsSet(implementedInterfaces: Array<ImplementedAntibodySchemaInterface>): Set<string> {
  return new Set(implementedInterfaces.map((implementedInterface) => implementedInterface.schema.id));
}

export interface AntibodySchemaFormatMaps {
  schemaIdToFormat: Map<string, AntibodyFormatGraphQL>;
  formatIdToSchemaIds: Map<string, Set<string>>;
  allSchemaIds: Set<string>;
}

/**
 * Builds maps and sets for navigating relationships between antibody schemas and formats from the implemented
 * antibody schema interfaces.
 */
export function buildAntibodySchemaFormatMaps(
  implementedAntibodySchemaInterfaces: Array<ImplementedAntibodySchemaInterface>
): AntibodySchemaFormatMaps {
  return {
    schemaIdToFormat: buildSchemaIdToFormatMap(implementedAntibodySchemaInterfaces),
    formatIdToSchemaIds: buildFormatIdToSchemaIdsMap(implementedAntibodySchemaInterfaces),
    allSchemaIds: buildAllSchemaIdsSet(implementedAntibodySchemaInterfaces),
  };
}

function _convertFrontendDistinctDomainKeyToBackendDistinctDomainKey(
  key: DistinctDomainKeyWithChainIndex
): DistinctDomainKeyDict {
  return {domainType: key.type, specificity: key.specificity};
}

function _prepareAntibodyDomainPairInput(pair: AntibodyDomainPairInput): Omit<
  AntibodyDomainPairInput,
  'pairKey'
> & {
  pairKey: {
    distinctDomainForPairVh: DistinctDomainKeyDict;
    distinctDomainForPairVl: DistinctDomainKeyDict;
  };
} {
  return {
    ...pair,
    pairKey: {
      distinctDomainForPairVh: _convertFrontendDistinctDomainKeyToBackendDistinctDomainKey(
        pair.pairKey.distinctDomainForPairVh
      ),
      distinctDomainForPairVl: _convertFrontendDistinctDomainKeyToBackendDistinctDomainKey(
        pair.pairKey.distinctDomainForPairVl
      ),
    },
  };
}

function _prepareAntibodyDomainInput(domain: AntibodyDomainInput): Omit<
  AntibodyDomainInput,
  'componentFormatKey'
> & {
  domainKey: DistinctDomainKeyDict;
} {
  const {componentFormatKey, ...rest} = domain;
  return {
    ...rest,
    domainKey: _convertFrontendDistinctDomainKeyToBackendDistinctDomainKey(componentFormatKey),
  };
}

function _prepareAntibodyChainInput(chain: AntibodyChainInput): Omit<
  AntibodyDomainInput,
  'componentFormatKey'
> & {
  chainKey: DistinctChainKeyDict;
} {
  const {componentFormatKey, ...rest} = chain;
  return {
    ...rest,
    chainKey: {label: componentFormatKey.label},
  };
}

/**
 * Prepares the antibody create inputs for the backend, removing frontend-only fields, and converting frontend
 * variations of DistinctDomainKey and DistinctChainKey to the backend types.
 */
export function prepareAntibodyCreateInputs(
  input: AntibodyPairAwareCreateInput
): AntibodyPairAwareCreateInputDict {
  return {
    pairs: input.pairs?.map((pair) => _prepareAntibodyDomainPairInput(pair)) ?? null,
    domains: input.domains?.map((domain) => _prepareAntibodyDomainInput(domain)) ?? null,
    chains: input.chains?.map((chain) => _prepareAntibodyChainInput(chain)) ?? null,
  };
}

/**
 * Generates spreadsheet template column headers based on the antibody format and starting type.
 *
 * Keep in sync with build_column_name_to_component_key in benchling/antibodies/nested_data_creation/spreadsheet_import/domain/infer_columns.py
 * Exported for testing.
 */
export function generateSpreadsheetTemplateHeaders(
  format: AntibodyFormat,
  startingType: NdcStartingType
): Array<string> {
  switch (startingType) {
    case NdcStartingType.DOMAINS:
      return format.distinctDomains.map((domain) => domain.label);
    case NdcStartingType.CHAINS:
      return format.distinctChains.map((chain) => chain.label);
    case NdcStartingType.PAIRS: {
      // The backend doesn't currently support pairs as inputs, but once implemented,
      // keep in sync with the expected column headers there.
      const pairHeaders = format.distinctVariablePairs.map((pair) => pair.label);

      const pairedDomainRefs = format.distinctVariablePairs.flatMap((pair) => [
        ...pair.vhVaVg.refs,
        ...pair.vlVbVd.refs,
      ]);
      const nonPairedDomainHeaders = format.distinctDomains
        .filter((domain) => !domain.refs.some((ref) => pairedDomainRefs.includes(ref)))
        .map((domain) => domain.label);

      return [...pairHeaders, ...nonPairedDomainHeaders];
    }
    default:
      throw new Error(`Unknown starting type: ${startingType}`);
  }
}

/**
 * Download a spreadsheet template with column headers based on the antibody format and starting type.
 *
 * The backend expects only component columns (domains or chains) in the spreadsheet.
 * Each row in the spreadsheet represents one antibody to be created.
 */
export function downloadSpreadsheetTemplate(format: AntibodyFormat, startingType: NdcStartingType): void {
  const headers = generateSpreadsheetTemplateHeaders(format, startingType);
  const csvContent = ExportUtil.formatRows([headers], ',');
  const startingTypeLabel = startingType.toLowerCase().replace(/ /g, '-');
  const sanitizedFormatName = format.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
  const filename = `protein-import-template-${sanitizedFormatName}-${startingTypeLabel}.csv`;

  initiateTextDownload(csvContent, filename);
}
